// src/pages/orders/editorJsDoc.js
// Read and write the EditorJS documents Saleor stored as product descriptions.
//
// WHY THIS EXISTS. Product copy was the last thing that still required Saleor: the
// engine stores the description as an EditorJS JSON string, the storefront parses
// that structure to render the PDP, and the dashboard could only show it read-only —
// because a plain textarea over the JSON would destroy it the moment anyone typed.
//
// Measured against the real catalog archive (all 6 products, 15 blocks): every block
// is a `paragraph` and the only inline markup is `<b>`. So this does not need an
// EditorJS build — it needs to edit block text and put the structure back intact.
//
// WHAT IS PRESERVED, and it is deliberate:
//   - each block's id, type and any extra data keys (a header's `level`, say)
//   - the document's `version` and any top-level fields we do not understand
//   - blocks with no editable text, which pass through byte-identical
// Only `data.text` is ever rewritten. A round trip with no edits produces an
// equivalent document, which is what makes this safe to ship over live copy.
//
// Inline HTML is shown RAW rather than hidden behind a rich-text toolbar. Five of the
// blocks carry <b>; a WYSIWYG that silently dropped it would quietly flatten the
// storefront's typography, and an operator who can see <b> can keep it.

// Recognise an EditorJS document. A plain-text description is not one, and must stay
// editable as plain text.
export function parseDoc(value) {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s.startsWith('{')) return null;
  let doc;
  try { doc = JSON.parse(s); } catch { return null; }
  if (!doc || !Array.isArray(doc.blocks)) return null;
  return doc;
}

// True when this block carries text a human can sensibly edit. An image or embed
// block has none, and must not be turned into an empty textarea.
export function isEditableBlock(block) {
  return !!(block && block.data && typeof block.data.text === 'string');
}

// Editable view of the document: one entry per block, editable ones carrying text.
export function toFields(doc) {
  return (doc.blocks || []).map((b, i) => ({
    index: i,
    id: b && b.id,
    type: (b && b.type) || 'paragraph',
    editable: isEditableBlock(b),
    text: isEditableBlock(b) ? b.data.text : '',
  }));
}

// Put the document back together.
//
// `texts` is index -> new text. Anything absent keeps its stored value, so a caller
// that edits one paragraph cannot blank the others — the same present-beats-absent
// rule the order-address edit needed after it silently erased customer names.
export function withTexts(doc, texts) {
  const blocks = (doc.blocks || []).map((b, i) => {
    if (!isEditableBlock(b)) return b;              // pass through untouched
    if (!(i in texts)) return b;
    return { ...b, data: { ...b.data, text: String(texts[i]) } };
  });
  // `time` is the document's own edit stamp, so moving it is honest. Every other
  // top-level field is carried through by the spread, including any this code has
  // never heard of.
  return { ...doc, blocks, time: Date.now() };
}

// Add an empty paragraph. The id is only for EditorJS's own bookkeeping; it must be
// unique within the document rather than globally.
export function appendParagraph(doc) {
  const id = `dash${Math.random().toString(36).slice(2, 12)}`;
  return { ...doc, blocks: [...(doc.blocks || []), { id, type: 'paragraph', data: { text: '' } }] };
}

export function removeBlock(doc, index) {
  return { ...doc, blocks: (doc.blocks || []).filter((_, i) => i !== index) };
}

// Serialize for the engine. Dropping empty paragraphs on the way out: an operator
// clearing a paragraph means "remove it", and an empty block renders as a blank gap
// on the product page.
export function serialize(doc) {
  const blocks = (doc.blocks || []).filter((b) => !(isEditableBlock(b) && b.data.text.trim() === ''));
  return JSON.stringify({ ...doc, blocks });
}

// Prose for a read-only preview: strips markup and decodes the entities EditorJS
// emits, so the description reads as text rather than source.
const ENTITIES = { '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'" };
export function toProse(doc) {
  return (doc.blocks || [])
    .filter(isEditableBlock)
    .map((b) => b.data.text
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;|&apos;/g, (m) => ENTITIES[m])
      .trim())
    .filter(Boolean)
    .join('\n\n');
}
